import { useState } from "react";
import { isWonStatus } from "@/data/quoteTypes";
import { useParams, Link } from "react-router-dom";
import { PermissionGate } from "@/components/PermissionGate";
import { useAppData } from "@/data/AppDataContext";
import { CustomerCommService } from "@/services/customer-comm.service";
import { buildJobDocuments } from "@/data/documentTypes";
import { getCustomerBlockers } from "@/data/unifiedBlockers";
import { getCustomerNextSteps } from "@/data/nextStepEngine";
import { RecordHealthPanel } from "@/components/RecordHealthPanel";
import { DocumentCenter } from "@/components/DocumentCenter";
import { CustomerMaterialsSection } from "@/components/jobspec/CustomerJobMaterialsSummary";
import { CustomerMessagesPanel } from "@/components/CustomerMessagesPanel";
import { CockpitPage, CockpitMasthead, HeroMetric, CockpitKpiStrip } from "@/components/cockpit/CockpitShell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "@/components/StatusBadge";
import { ActivityTimeline } from "@/components/ActivityTimeline";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft, Phone, Mail, MapPin, Briefcase, AlertTriangle,
  CheckCircle, FileText, Clock, MessageSquare, PhoneCall, MessageCircle,
  AtSign, CalendarCheck, ShieldAlert, Bell, StickyNote, Plus, ExternalLink,
  ChevronDown, ChevronRight, Package,
} from "lucide-react";
import type { CommType, CommSource, CustomerCommEntry } from "@/data/types";
import { CreditCard as CreditIcon } from "lucide-react";
import { formatAddress } from "@/lib/addressUtils";
import { getJobDisplayValue, getCustomerLifetimeValue } from "@/data/jobPricingSelectors";
import { useFinancialPermissions } from "@/hooks/useFinancialPermissions";
import { cn } from "@/lib/utils";

const COMM_ICONS: Record<CommType, React.ElementType> = {
  "Phone Call": PhoneCall,
  "Text": MessageCircle,
  "Email": AtSign,
  "Appointment Confirmation": CalendarCheck,
  "Issue Follow-up": ShieldAlert,
  "Service Update": Bell,
  "Internal Note": StickyNote,
};

const COMM_COLORS: Record<CommType, string> = {
  "Phone Call": "bg-primary/10 text-primary border-primary/20",
  "Text": "bg-info/10 text-info border-info/20",
  "Email": "bg-accent/10 text-accent border-accent/20",
  "Appointment Confirmation": "bg-success/10 text-success border-success/20",
  "Issue Follow-up": "bg-destructive/10 text-destructive border-destructive/20",
  "Service Update": "bg-warning/10 text-warning border-warning/20",
  "Internal Note": "bg-muted text-muted-foreground border-border",
};

const SOURCE_BADGE: Record<CommSource, string> = {
  "Manual": "bg-muted text-muted-foreground",
  "GHL": "bg-primary/10 text-primary",
  "External": "bg-accent/10 text-accent",
};

const ALL_COMM_TYPES: CommType[] = ["Phone Call", "Text", "Email", "Appointment Confirmation", "Issue Follow-up", "Service Update", "Internal Note"];

/* ─────────── Cockpit panel — hairline, no shadow, matches Conversations ─────────── */
function Panel({ title, icon: Icon, count, action, children, className }: {
  title?: string; icon?: React.ElementType; count?: number;
  action?: React.ReactNode; children: React.ReactNode; className?: string;
}) {
  return (
    <section className={cn("border border-border/60 rounded-md bg-background", className)}>
      {title && (
        <header className="flex items-center justify-between px-4 py-2.5 border-b border-border/60">
          <h2 className="text-[11px] font-semibold text-muted-foreground uppercase tracking-[0.14em] flex items-center gap-2">
            {Icon && <Icon className="h-3.5 w-3.5" />} {title}
            {typeof count === "number" && <span className="tabular-nums text-muted-foreground/70">({count})</span>}
          </h2>
          {action}
        </header>
      )}
      <div className="p-4">{children}</div>
    </section>
  );
}

export default function CustomerProfile() {
  const { name } = useParams<{ name: string }>();
  const { jobs, issues, customerComms, setCustomerComms, credits, quotes, serviceTickets } = useAppData();
  const fin$ = useFinancialPermissions();
  const [showAddComm, setShowAddComm] = useState(false);
  const [commFilter, setCommFilter] = useState<string>("all");
  const [expandedJob, setExpandedJob] = useState<string | null>(null);

  const visibleCommTypes: CommType[] = fin$.viewInternalNotes
    ? ALL_COMM_TYPES
    : ALL_COMM_TYPES.filter((t) => t !== "Internal Note");

  const [newCommType, setNewCommType] = useState<CommType>("Phone Call");
  const [newCommSummary, setNewCommSummary] = useState("");
  const [newCommJob, setNewCommJob] = useState("");
  const [newCommSource, setNewCommSource] = useState<CommSource>("Manual");

  const routeKey = decodeURIComponent(name || "").trim();
  const customerJobs = jobs.filter((j) => {
    const customerId = String((j as { customerId?: string }).customerId ?? "").trim();
    return customerId === routeKey || j.customerName === routeKey;
  });

  if (customerJobs.length === 0) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        <p>Customer not found.</p>
        <Link to="/customers" className="text-primary hover:underline text-sm mt-2 inline-block">Back to Customers</Link>
      </div>
    );
  }

  const ref = customerJobs[0];
  const openJobs = customerJobs.filter((j) => j.currentStage !== "Completed");
  const pastJobs = customerJobs.filter((j) => j.currentStage === "Completed");
  const customerIssues = issues.filter((i) => customerJobs.some((j) => j.id === i.jobId));
  const canonicalCustomerId = String((customerJobs[0] as { customerId?: string }).customerId ?? "").trim() || undefined;
  const customerName = customerJobs[0]?.customerName ?? routeKey;
  const customerQuotes = quotes.filter((q) => {
    const qCustomerId = String((q as { customerId?: string }).customerId ?? "").trim();
    return (canonicalCustomerId && qCustomerId === canonicalCustomerId) || q.customerName === customerName;
  });
  const openIssues = customerIssues.filter((i) => i.status === "Open" || i.status === "In Progress");
  const resolvedIssues = customerIssues.filter((i) => i.status === "Resolved" || i.status === "Closed");
  const totalValue = getCustomerLifetimeValue(customerJobs);
  const customerAddressCandidates = customerJobs
    .map((j) => formatAddress(j.address).trim())
    .filter(Boolean);
  const customerFullAddress = customerAddressCandidates
    .sort((a, b) => {
      const aHasStreet = /\d/.test(a);
      const bHasStreet = /\d/.test(b);
      if (aHasStreet !== bHasStreet) return aHasStreet ? -1 : 1;
      return b.length - a.length;
    })[0] || "";

  const jobIds = new Set(customerJobs.map((j) => j.id));
  const comms = customerComms
    .filter((c) => c.relatedJobId ? jobIds.has(c.relatedJobId) : false)
    .filter((c) => fin$.viewInternalNotes || c.commType !== "Internal Note")
    .sort((a, b) => `${b.date} ${b.time}`.localeCompare(`${a.date} ${a.time}`));

  const filteredComms = commFilter === "all" ? comms : comms.filter((c) => c.commType === commFilter);

  const isUuidLike = (value: string) =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(String(value).trim());

  const displayJobRef = (jobId: string) => {
    const match = customerJobs.find((j) => j.id === jobId);
    if (match?.jobNumber) return match.jobNumber;
    return isUuidLike(jobId) ? "Job" : jobId;
  };

  const displayUserRef = (value: string) => {
    const v = String(value ?? "").trim();
    if (!v) return "System";
    return isUuidLike(v) ? "System" : v;
  };

  const scrubUuidFromText = (text: string) => {
    if (!text) return text;
    return text.replace(/[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/gi, "record");
  };

  const handleAddComm = async () => {
    if (!newCommSummary.trim()) return;
    const now = new Date();
    const entry: CustomerCommEntry = {
      id: `COMM-${Date.now()}`,
      date: now.toISOString().split("T")[0],
      time: now.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }),
      commType: newCommType,
      summary: newCommSummary.trim(),
      relatedJobId: newCommJob || undefined,
      user: "Current User",
      source: newCommSource,
    };
    const created = await CustomerCommService.createCustomerComm(entry);
    if (created) {
      setCustomerComms((prev) => [created, ...prev]);
    } else {
      setCustomerComms((prev) => [entry, ...prev]);
    }
    setNewCommSummary("");
    setNewCommJob("");
    setShowAddComm(false);
  };

  const allActivity = customerJobs
    .flatMap((j) => j.activityHistory.map((a) => ({
      ...a,
      action: scrubUuidFromText(`[${displayJobRef(j.id)}] ${a.action}`),
      user: displayUserRef(a.user),
    })))
    .sort((a, b) => b.date.localeCompare(a.date));

  const eyebrowParts = [ref.branch, customerFullAddress].filter(Boolean).join(" · ");

  /* ─────────── VERDICT (one-line status + next action) ─────────── */
  const blockers = getCustomerBlockers(
    customerJobs, customerQuotes,
    serviceTickets.filter((t) => (canonicalCustomerId && t.customerId === canonicalCustomerId) || jobIds.has(t.jobId)),
    issues, quotes,
  );
  const nextSteps = getCustomerNextSteps(
    customerJobs, customerQuotes,
    serviceTickets.filter((t) => (canonicalCustomerId && t.customerId === canonicalCustomerId) || jobIds.has(t.jobId)),
    issues, quotes,
  );
  const blockerCount = blockers.length;
  const nextAction = nextSteps[0]?.action || (openJobs.length === 0 ? "No open jobs" : "On track — no immediate action");
  const verdict: { label: string; tone: "good" | "warn" | "bad" } =
    blockerCount > 0 || openIssues.length > 0
      ? { label: blockerCount > 0 ? "At Risk" : "Action Needed", tone: blockerCount > 0 ? "bad" : "warn" }
      : { label: "Healthy", tone: "good" };

  const verdictTone =
    verdict.tone === "bad"  ? "border-destructive/30 bg-destructive/[0.04] text-destructive"
    : verdict.tone === "warn" ? "border-warning/30 bg-warning/[0.04] text-warning"
    : "border-success/30 bg-success/[0.04] text-success";

  const customerCredits = credits.filter((c) => (canonicalCustomerId && c.customerId === canonicalCustomerId) || jobIds.has(c.jobId));

  return (
    <CockpitPage className="max-w-[1400px]">
      {/* Top toolbar */}
      <div className="flex items-center justify-between gap-3 -mb-2">
        <Link to="/customers" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to Customers
        </Link>
        <div className="flex items-center gap-1.5 flex-wrap">
          <Button asChild variant="outline" size="sm" className="h-7 gap-1 text-[11px]">
            <a href={`tel:${ref.phone}`}><Phone className="h-3 w-3" /> Call</a>
          </Button>
          {ref.email && (
            <Button asChild variant="outline" size="sm" className="h-7 gap-1 text-[11px]">
              <a href={`mailto:${ref.email}`}><Mail className="h-3 w-3" /> Email</a>
            </Button>
          )}
        </div>
      </div>

      <CockpitMasthead
        eyebrow={`Customer${eyebrowParts ? ` · ${eyebrowParts}` : ""} · ${customerJobs.length} job${customerJobs.length === 1 ? "" : "s"}`}
        title={customerName}
        right={fin$.viewContractValue
          ? <HeroMetric label={fin$.label("Lifetime Value")} value={`$${totalValue.toLocaleString()}`} hero tone="default" />
          : undefined}
      />

      <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap -mt-2">
        <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{customerFullAddress || "Address unavailable"}</span>
        <span className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{ref.phone}</span>
        {ref.email && <span className="inline-flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{ref.email}</span>}
      </div>

      {ref.secondaryContactName && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground border-y border-border/60 py-2">
          <span className="text-[10px] uppercase tracking-[0.16em] font-medium text-muted-foreground">Secondary</span>
          <span className="font-medium text-foreground">{ref.secondaryContactName}</span>
          {ref.secondaryContactPhone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {ref.secondaryContactPhone}</span>}
          {ref.secondaryContactEmail && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" /> {ref.secondaryContactEmail}</span>}
        </div>
      )}

      <CockpitKpiStrip
        items={[
          { label: "Total Jobs", value: customerJobs.length },
          { label: "Open", value: openJobs.length, tone: openJobs.length > 0 ? "good" : "default" },
          { label: "Completed", value: pastJobs.length },
          { label: "Open Issues", value: openIssues.length, tone: openIssues.length > 0 ? "bad" : "default" },
          ...(fin$.viewContractValue ? [] : [{ label: "Records", value: customerJobs.length + customerQuotes.length }]),
        ]}
      />

      {/* ═══ VERDICT BAR — one-line status + next action ═══ */}
      <div className={cn("border rounded-md px-4 py-2.5 flex items-center gap-4 flex-wrap", verdictTone)}>
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-[0.16em] font-semibold opacity-70">Status</span>
          <span className="text-sm font-bold">{verdict.label}</span>
        </div>
        <span className="h-3 w-px bg-current opacity-20" />
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className="text-[10px] uppercase tracking-[0.16em] font-semibold opacity-70">Next</span>
          <span className="text-sm text-foreground truncate">{nextAction}</span>
        </div>
        <div className="flex items-center gap-1.5 text-[10px] text-foreground/70 tabular-nums">
          <span>{blockerCount} blocker{blockerCount === 1 ? "" : "s"}</span>
          <span className="opacity-40">·</span>
          <span>{openIssues.length} issue{openIssues.length === 1 ? "" : "s"}</span>
          <span className="opacity-40">·</span>
          <span>{openJobs.length} active job{openJobs.length === 1 ? "" : "s"}</span>
        </div>
      </div>

      <div className="grid gap-4 grid-cols-1 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-4 min-w-0">

          {/* ═══ OPEN JOBS — primary surface, materials fold inline ═══ */}
          {openJobs.length > 0 && (
            <Panel title="Open Jobs" icon={Briefcase} count={openJobs.length}>
              <div className="divide-y divide-border/60 -mx-4 -my-4">
                {openJobs.map((job) => {
                  const isOpen = expandedJob === job.id;
                  return (
                    <div key={job.id} className="px-4 py-3">
                      <div className="flex items-center justify-between gap-3">
                        <Link to={`/jobs/${job.id}`} className="flex items-center gap-3 min-w-0 flex-1 hover:opacity-80">
                          <span className="font-mono font-semibold text-xs text-primary shrink-0">{displayJobRef(job.id)}</span>
                          <span className="text-xs font-medium truncate">{job.projectType}</span>
                          <span className="text-[10px] text-muted-foreground hidden sm:inline truncate">
                            {job.assignedCrew || job.assignedSubcontractor || "Unassigned"}
                          </span>
                        </Link>
                        <div className="flex items-center gap-2 shrink-0">
                          <StatusBadge type="stage" value={job.currentStage} />
                          {fin$.viewContractValue && (
                            <span className="text-xs text-muted-foreground tabular-nums">${getJobDisplayValue(job).toLocaleString()}</span>
                          )}
                          <button
                            onClick={() => setExpandedJob(isOpen ? null : job.id)}
                            className="inline-flex items-center gap-1 text-[10px] font-semibold text-muted-foreground hover:text-foreground border border-border/60 rounded px-1.5 py-0.5 transition-colors"
                          >
                            <Package className="h-3 w-3" /> Materials
                            {isOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                          </button>
                        </div>
                      </div>
                      {isOpen && (
                        <div className="mt-3 pl-1 border-l-2 border-border/60 ml-1">
                          <div className="pl-3">
                            <CustomerMaterialsSection jobs={[job]} />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </Panel>
          )}

          {/* ═══ HEALTH — auto-folds to summary unless blockers exist ═══ */}
          <Panel>
            <RecordHealthPanel
              title="Customer Health"
              blockers={blockers}
              nextSteps={nextSteps}
              completeness={[
                { label: "Phone", done: !!ref.phone },
                { label: "Email", done: !!ref.email },
                { label: "Address", done: !!ref.address },
                { label: "Active Quote or Job", done: customerQuotes.length > 0 || customerJobs.length > 0 },
              ]}
              compact
            />
          </Panel>

          {/* ═══ TABS — Timeline · History · Issues · Documents ═══ */}
          <Tabs defaultValue="timeline" className="w-full">
            <TabsList className="bg-transparent border-b border-border/60 rounded-none w-full justify-start h-auto p-0 gap-0">
              <TabsTrigger value="timeline" className="rounded-none border-b-2 border-transparent data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none px-3 py-2 text-xs">
                Timeline
              </TabsTrigger>
              <TabsTrigger value="history" className="rounded-none border-b-2 border-transparent data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none px-3 py-2 text-xs">
                History {pastJobs.length > 0 && <span className="ml-1 text-muted-foreground">({pastJobs.length})</span>}
              </TabsTrigger>
              <TabsTrigger value="issues" className="rounded-none border-b-2 border-transparent data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none px-3 py-2 text-xs">
                Issues {customerIssues.length > 0 && <span className="ml-1 text-muted-foreground">({customerIssues.length})</span>}
              </TabsTrigger>
              <TabsTrigger value="docs" className="rounded-none border-b-2 border-transparent data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none px-3 py-2 text-xs">
                Documents
              </TabsTrigger>
              {fin$.viewContractValue && customerCredits.length > 0 && (
                <TabsTrigger value="finance" className="rounded-none border-b-2 border-transparent data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none px-3 py-2 text-xs">
                  Finance
                </TabsTrigger>
              )}
            </TabsList>

            {/* ── TIMELINE: unified comm log + activity ── */}
            <TabsContent value="timeline" className="mt-3 space-y-3">
              <Panel
                title="Communication"
                icon={MessageSquare}
                count={comms.length}
                action={
                  <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => setShowAddComm(!showAddComm)}>
                    <Plus className="h-3 w-3" /> Log Entry
                  </Button>
                }
              >
                {showAddComm && (
                  <div className="mb-4 p-3 rounded-md border border-border/60 bg-muted/20 space-y-3">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      <div className="space-y-1">
                        <Label className="text-[10px]">Type</Label>
                        <Select value={newCommType} onValueChange={(v) => setNewCommType(v as CommType)}>
                          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {visibleCommTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px]">Related Job</Label>
                        <Select value={newCommJob} onValueChange={setNewCommJob}>
                          <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Optional" /></SelectTrigger>
                          <SelectContent>
                            {customerJobs.map((j) => <SelectItem key={j.id} value={j.id}>{displayJobRef(j.id)} — {j.projectType}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px]">Source</Label>
                        <Select value={newCommSource} onValueChange={(v) => setNewCommSource(v as CommSource)}>
                          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Manual">Manual</SelectItem>
                            <SelectItem value="GHL">GHL</SelectItem>
                            <SelectItem value="External">External</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px]">Summary</Label>
                      <Textarea value={newCommSummary} onChange={(e) => setNewCommSummary(e.target.value)} placeholder="Describe the interaction..." rows={2} className="text-xs" />
                    </div>
                    <div className="flex gap-2">
                      <PermissionGate permission="customers_edit">
                        <Button size="sm" onClick={handleAddComm} disabled={!newCommSummary.trim()}>Save Entry</Button>
                      </PermissionGate>
                      <Button size="sm" variant="ghost" onClick={() => setShowAddComm(false)}>Cancel</Button>
                    </div>
                  </div>
                )}

                <div className="flex gap-1.5 flex-wrap mb-3">
                  <button
                    onClick={() => setCommFilter("all")}
                    className={cn(
                      "rounded-sm border px-2.5 py-1 text-[10px] font-semibold transition-colors",
                      commFilter === "all" ? "bg-foreground text-background border-foreground" : "bg-transparent text-muted-foreground border-border/60 hover:text-foreground"
                    )}
                  >
                    All ({comms.length})
                  </button>
                  {visibleCommTypes.map((t) => {
                    const count = comms.filter((c) => c.commType === t).length;
                    if (count === 0) return null;
                    const Icon = COMM_ICONS[t];
                    return (
                      <button
                        key={t}
                        onClick={() => setCommFilter(commFilter === t ? "all" : t)}
                        className={cn(
                          "rounded-sm border px-2.5 py-1 text-[10px] font-semibold transition-colors inline-flex items-center gap-1",
                          commFilter === t ? "bg-foreground text-background border-foreground" : "bg-transparent text-muted-foreground border-border/60 hover:text-foreground"
                        )}
                      >
                        <Icon className="h-3 w-3" /> {t} ({count})
                      </button>
                    );
                  })}
                </div>

                {filteredComms.length === 0 ? (
                  <div className="flex items-center justify-center py-8 text-xs text-muted-foreground">
                    <MessageSquare className="h-4 w-4 mr-2 opacity-40" /> No communication entries
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredComms.map((c) => {
                      const Icon = COMM_ICONS[c.commType];
                      return (
                        <div key={c.id} className="flex gap-3 p-3 rounded-md border border-border/60 hover:bg-muted/20 transition-colors">
                          <div className={cn("h-8 w-8 rounded-md flex items-center justify-center shrink-0 border", COMM_COLORS[c.commType])}>
                            <Icon className="h-4 w-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={cn("inline-flex items-center rounded-sm border px-1.5 py-0.5 text-[10px] font-semibold", COMM_COLORS[c.commType])}>
                                {c.commType}
                              </span>
                              <span className={cn("inline-flex items-center rounded-sm px-1.5 py-0.5 text-[10px] font-medium", SOURCE_BADGE[c.source])}>
                                {c.source}
                              </span>
                              {c.relatedJobId && (
                                <Link to={`/jobs/${c.relatedJobId}`} className="text-[10px] font-mono text-primary hover:underline inline-flex items-center gap-0.5">
                                  <ExternalLink className="h-2.5 w-2.5" />{displayJobRef(c.relatedJobId)}
                                </Link>
                              )}
                            </div>
                            <p className="text-xs mt-1">{c.summary}</p>
                            <div className="flex items-center gap-3 mt-1.5 text-[10px] text-muted-foreground">
                              <span>{c.date} · {c.time}</span>
                              <span>by {displayUserRef(c.user)}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </Panel>

              <Panel title="Recent Activity" icon={Clock}>
                {allActivity.length > 0
                  ? <ActivityTimeline entries={allActivity} />
                  : <p className="text-xs text-muted-foreground text-center py-4">No activity recorded.</p>}
              </Panel>
            </TabsContent>

            {/* ── HISTORY: completed jobs + quotes + contracts ── */}
            <TabsContent value="history" className="mt-3 space-y-3">
              {pastJobs.length > 0 && (
                <Panel title="Completed Jobs" icon={CheckCircle} count={pastJobs.length}>
                  <div className="space-y-2">
                    {pastJobs.map((job) => (
                      <Link key={job.id} to={`/jobs/${job.id}`} className="block">
                        <div className="flex items-center justify-between p-3 rounded-md border border-border/60 bg-muted/10 hover:bg-muted/30 transition-colors">
                          <div className="flex items-center gap-3">
                            <span className="font-mono font-semibold text-xs text-primary">{displayJobRef(job.id)}</span>
                            <span className="text-xs font-medium">{job.projectType}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <StatusBadge type="closeout" value={job.closeoutStatus} />
                            {fin$.viewContractValue && (
                              <span className="text-xs text-muted-foreground tabular-nums">${getJobDisplayValue(job).toLocaleString()}</span>
                            )}
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </Panel>
              )}

              {customerQuotes.length > 0 && (
                <Panel title="Quotes" icon={FileText} count={customerQuotes.length}>
                  <div className="space-y-2">
                    {customerQuotes.map((q) => (
                      <Link key={q.id} to={`/opportunities/${q.id}`} className="block">
                        <div className="flex items-center justify-between p-3 rounded-md border border-border/60 hover:bg-muted/30 transition-colors">
                          <div className="flex items-center gap-3">
                            <span className="font-mono font-semibold text-xs text-primary">{q.displayReference || (isUuidLike(q.id) ? "Quote" : q.id)}</span>
                            <span className="text-xs font-medium">{q.projectConfig?.selectedVerticals?.join(", ") || "General"}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant={isWonStatus(q.status) ? "default" : q.status === "Closed Lost" ? "destructive" : "secondary"} className="text-[10px]">
                              {q.status}
                            </Badge>
                            {fin$.viewContractValue && (
                              <span className="text-xs text-muted-foreground tabular-nums">${q.totalEstimate.toLocaleString()}</span>
                            )}
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </Panel>
              )}

              {(() => {
                const jobsWithContracts = customerJobs.filter((j) => j.contractDocument);
                if (jobsWithContracts.length === 0 || !fin$.viewContractValue) return null;
                return (
                  <Panel title="Contracts & Change Orders" icon={FileText}>
                    <div className="space-y-3">
                      {jobsWithContracts.map((j) => {
                        const contract = j.contractDocument!;
                        const changeOrders = j.changeOrders ?? [];
                        const signedCOs = changeOrders.filter((co) => co.status === "Signed");
                        const totalCOValue = signedCOs.reduce((s, co) => s + co.changeAmount, 0);
                        return (
                          <div key={j.id} className="rounded-md border border-border/60 p-3">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <Link to={`/jobs/${j.id}`} className="font-mono text-xs text-primary hover:underline font-semibold">{displayJobRef(j.id)}</Link>
                                <span className="text-xs font-medium">{j.projectType}</span>
                              </div>
                              <Badge variant={contract.status === "Signed" ? "default" : "secondary"} className="text-[10px]">{contract.status}</Badge>
                            </div>
                            <div className="grid grid-cols-3 gap-2 text-center">
                              <div className="p-2 rounded-sm border border-border/60 bg-muted/20">
                                <p className="text-[9px] text-muted-foreground uppercase tracking-wider font-semibold">Original</p>
                                <p className="text-sm font-bold tabular-nums">${(contract.contractTotal ?? getJobDisplayValue(j)).toLocaleString()}</p>
                              </div>
                              <div className="p-2 rounded-sm border border-border/60 bg-muted/20">
                                <p className="text-[9px] text-muted-foreground uppercase tracking-wider font-semibold">Change Orders</p>
                                <p className={cn("text-sm font-bold tabular-nums", totalCOValue !== 0 && "text-primary")}>
                                  {signedCOs.length > 0 ? `$${totalCOValue.toLocaleString()} (${signedCOs.length})` : "—"}
                                </p>
                              </div>
                              <div className="p-2 rounded-sm border border-border/60 bg-muted/20">
                                <p className="text-[9px] text-muted-foreground uppercase tracking-wider font-semibold">Current</p>
                                <p className="text-sm font-bold text-primary tabular-nums">${getJobDisplayValue(j).toLocaleString()}</p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </Panel>
                );
              })()}

              {pastJobs.length === 0 && customerQuotes.length === 0 && (
                <div className="text-center py-8 text-xs text-muted-foreground">No history yet.</div>
              )}
            </TabsContent>

            {/* ── ISSUES ── */}
            <TabsContent value="issues" className="mt-3">
              <Panel title="Issues" icon={AlertTriangle} count={customerIssues.length}>
                {customerIssues.length === 0 ? (
                  <div className="flex items-center justify-center py-6 text-xs text-muted-foreground">
                    <CheckCircle className="h-4 w-4 mr-2 text-primary" /> No issues on record
                  </div>
                ) : (
                  <div className="space-y-3">
                    {openIssues.length > 0 && (
                      <div>
                        <p className="text-[10px] font-semibold text-destructive uppercase tracking-[0.12em] mb-2">Open ({openIssues.length})</p>
                        <div className="space-y-2">
                          {openIssues.map((i) => (
                            <div key={i.id} className="p-3 rounded-md border border-destructive/20 bg-destructive/[0.03]">
                              <div className="flex items-center justify-between gap-2 mb-1">
                                <div className="flex items-center gap-2">
                                  <Link to={`/jobs/${i.jobId}`} className="font-mono text-[10px] text-primary hover:underline">{displayJobRef(i.jobId)}</Link>
                                  <span className="font-semibold text-xs">{i.issueType}</span>
                                </div>
                                <div className="flex gap-1.5">
                                  <StatusBadge type="severity" value={i.severity} />
                                  <StatusBadge type="issueStatus" value={i.status} />
                                </div>
                              </div>
                              <p className="text-xs text-muted-foreground">{i.description}</p>
                              <p className="text-[10px] text-muted-foreground mt-1">Owner: {i.owner} · Source: {i.source} · {i.dateCreated}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {resolvedIssues.length > 0 && (
                      <div>
                        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.12em] mb-2">Resolved ({resolvedIssues.length})</p>
                        <div className="space-y-2">
                          {resolvedIssues.map((i) => (
                            <div key={i.id} className="p-3 rounded-md border border-border/60 bg-muted/20">
                              <div className="flex items-center justify-between gap-2 mb-1">
                                <div className="flex items-center gap-2">
                                  <Link to={`/jobs/${i.jobId}`} className="font-mono text-[10px] text-primary hover:underline">{displayJobRef(i.jobId)}</Link>
                                  <span className="font-medium text-xs text-muted-foreground">{i.issueType}</span>
                                </div>
                                <StatusBadge type="issueStatus" value={i.status} />
                              </div>
                              <p className="text-xs text-muted-foreground">{i.description}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </Panel>
            </TabsContent>

            {/* ── DOCUMENTS + NOTES ── */}
            <TabsContent value="docs" className="mt-3 space-y-3">
              <Panel title="Documents" icon={FileText}>
                 {(() => {
                   const allDocs = customerJobs.flatMap(j =>
                     buildJobDocuments(
                       j,
                       quotes.map(q => ({
                         id: q.id,
                         displayReference: q.displayReference,
                         quoteNumber: q.quote_number,
                         status: q.stage,
                         createdAt: q.createdAt,
                         createdBy: q.assignedRep || "System",
                         updatedAt: q.updatedAt,
                         customerName: q.primaryName
                       })),
                       issues
                     )
                   );
                   return allDocs.length > 0
                     ? <DocumentCenter documents={allDocs} compact />
                     : <p className="text-xs text-muted-foreground text-center py-4">No documents on record.</p>;
                 })()}
              </Panel>
              {fin$.viewInternalNotes && customerJobs.some(j => j.notes) && (
                <Panel title="Notes" icon={StickyNote}>
                  <div className="space-y-2">
                    {customerJobs.map((job) =>
                      job.notes ? (
                        <div key={job.id} className="p-3 rounded-md bg-muted/20 border border-border/60">
                          <p className="text-[10px] font-semibold text-muted-foreground mb-1">{displayJobRef(job.id)} · {job.projectType}</p>
                          <p className="text-xs text-muted-foreground">{job.notes}</p>
                        </div>
                      ) : null
                    )}
                  </div>
                </Panel>
              )}
            </TabsContent>

            {/* ── FINANCE (gated) ── */}
            {fin$.viewContractValue && customerCredits.length > 0 && (
              <TabsContent value="finance" className="mt-3">
                <Panel title="Credits & Resolutions" icon={CreditIcon} count={customerCredits.length}>
                  <div className="rounded-md border border-border/60 bg-muted/20 p-3 mb-3 text-center">
                    <p className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] font-semibold">Total Credits</p>
                    <p className="text-lg font-bold text-primary tabular-nums">${customerCredits.reduce((s, c) => s + c.creditAmount, 0).toLocaleString()}</p>
                    <p className="text-[10px] text-muted-foreground">{customerCredits.length} record{customerCredits.length !== 1 ? "s" : ""}</p>
                  </div>
                  <div className="space-y-2">
                    {customerCredits.map((cr) => (
                      <div key={cr.id} className="rounded-md border border-border/60 p-3 bg-muted/10">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <Link to={`/jobs/${cr.jobId}`} className="font-mono text-[10px] text-primary hover:underline">{displayJobRef(cr.jobId)}</Link>
                            <span className="text-xs font-semibold">{cr.creditType}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-bold text-primary tabular-nums">${cr.creditAmount.toLocaleString()}</span>
                            <StatusBadge type="creditStatus" value={cr.status} />
                          </div>
                        </div>
                        <p className="text-[11px] text-muted-foreground">{cr.reasonCategory} · {cr.notes}</p>
                        <p className="text-[10px] text-muted-foreground mt-1">
                          {cr.dateCreated} · {cr.createdBy}
                          {cr.issueId && <> · Issue: {cr.issueId}</>}
                        </p>
                      </div>
                    ))}
                  </div>
                </Panel>
              </TabsContent>
            )}
          </Tabs>

          {/* Inline Messages on narrow widths */}
          {customerJobs.length > 0 && (
            <div className="xl:hidden h-[440px]">
              <CustomerMessagesPanel customerJobs={customerJobs} customerName={customerName} />
            </div>
          )}
        </div>

        {/* Right rail — Messages (xl+) */}
        {customerJobs.length > 0 && (
          <aside className="hidden xl:block">
            <div className="sticky top-4 h-[calc(90vh-2rem)]">
              <CustomerMessagesPanel customerJobs={customerJobs} customerName={customerName} />
            </div>
          </aside>
        )}
      </div>
    </CockpitPage>
  );
}
